import { useEffect, useRef } from 'react';

import { useQuery } from '@apollo/client';

import { queries } from '@client/common/graph';

import { shouldFetchPredictions } from '../StandardSelect/StandardSelect';

export function useRecommendations ({ content, parent }) {
  const prevContentKeys = useRef();
  const firstRun = useRef(true);

  const skip = !shouldFetchPredictions(content);

  // Limit combined concepts and standards to 290 items
  const MAX_ITEMS = 290;
  const concepts = (content.metadataCerticaConcepts || []).map((tag) => tag.id).sort();
  const standards = (content.metadataStandards?.map((item) => item.id) || []).sort();
  const totalCount = concepts.length + standards.length;

  let limitedConcepts = concepts;
  let limitedStandards = standards;

  if (totalCount > MAX_ITEMS) {
    // Proportionally distribute the limit between concepts and standards
    const conceptsRatio = concepts.length / totalCount;
    const maxConcepts = Math.floor(MAX_ITEMS * conceptsRatio);
    const maxStandards = MAX_ITEMS - maxConcepts;

    limitedConcepts = concepts.slice(0, maxConcepts);
    limitedStandards = standards.slice(0, maxStandards);
  }

  const variables = {
    certicaId: content?.certicaId,
    uid: content?.uid,
    metadataConcepts: limitedConcepts,
    metadataStandards: limitedStandards,
    maturityRange: content?.maturityRange || [],
  };

  const {
    data,
    loading,
    error,
    refetch,
  } = useQuery(queries.predictedStandards, {
    skip,
    variables,
  });

  const getContentKeys = (content) => ({
    certicaId: content?.certicaId,
    standardIds: (content?.metadataStandards || []).map((s) => s.id).sort(),
    conceptIds: (content?.metadataCerticaConcepts || []).map((tag) => tag.id).sort()
  });

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      prevContentKeys.current = getContentKeys(content);
      return;
    }
    const currentKeys = getContentKeys(content);

    const shouldRefetch = JSON.stringify(prevContentKeys.current) !== JSON.stringify(currentKeys);

    if (shouldRefetch && !skip) {
      prevContentKeys.current = currentKeys;
      refetch();
    }
    // eslint-disable-next-line
  }, [
    content?.certicaId,
    content?.metadataStandards,
    content?.metadataCerticaConcepts,
    skip,
    refetch,
  ]);

  return {
    data,
    loading,
    error,
    refetch,
  };
}
